/**
 * B-408 — zakładka „Weryfikacja CEPiK” w Kierowcach.
 *
 * Widoki: Kierowcy (stan z CEPiK, zaznaczanie i sprawdzanie masowe), Alerty
 * (potwierdzanie z notatką), Wygasania (kalendarz 90 dni). Szczegóły kierowcy
 * w szufladzie: porównanie GAiT ↔ CEPiK, służba dziś/jutro, historia sprawdzeń.
 * Harmonogram i historia przebiegów — w Ustawieniach systemowych (sekcja „Weryfikacja CEPiK”).
 */
import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { AlertMailButton, AlertMailHistory } from './CepikAlertMail';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck, Users, Loader2, RefreshCw, X, Bell, CalendarRange, FileDown, Settings2, Square, CheckSquare,
  MinusSquare, Bus, TramFront, MapPin, CalendarClock, History, AlertTriangle, CheckCircle2, Clock,
  PlayCircle, StopCircle, ShieldX, Info, BellRing,
} from 'lucide-react';
import { api } from '@/shared/lib/api';
import { cn } from '@/shared/lib/utils';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { ViewHero } from '@/shared/components/ViewHero';
import {
  HeroKpi, HeroHealthBar, FilterSelect, QuickChip, Segmented, SearchBox, ActiveFilterBar, TONE,
  type ActiveFilter,
} from '@/shared/components/ListPageKit';
import { DepotChip, depotLabel } from '@/shared/components/DepotChip';
import {
  BUCKET, BUCKET_ORDER, STATUS_LABEL, LOSS, TRIGGER_LABEL, RUN_STATUS, BucketPill, NOT_WORK_HINT, catValidity, lossSummary, problemText, realReasons, todayPl, workCategories, fmtDate, fmtDateTime, fmtTime,
  daysLeft, relDays, downloadFromApi, errMsg,
  type Bucket, type CepikAlert, type CepikCheck, type CepikDriverRow, type CepikOverview, type CepikRun,
} from './cepikShared';

import { Lista } from '@/shared/components/ui/Lista';
type SubView = 'drivers' | 'alerts' | 'expiry';
/** B-705: filtr kierowcy / motorniczowie — w widoku, bo raport PDF generuje się w jego kontekście. Brak typu w kartotece = kierowca. */
type DriverTypeFilter = '' | 'DRIVER' | 'MOTORMAN';
/** B-705: `issues` = wszystko poza „Ważne” (stan z ostatniego sprawdzenia osoby) — widok domyślny. */
type StateFilter = Bucket | '' | 'issues';
const STALE_OPTS = [
  { value: '', label: 'Sprawdzani kiedykolwiek' },
  { value: '1', label: 'Nie sprawdzani od 1 dnia' },
  { value: '7', label: 'Nie sprawdzani od 7 dni' },
  { value: '30', label: 'Nie sprawdzani od 30 dni' },
];

/** B-705: własna strona `/admin/cepik` (pozycja „Weryfikacja CEPiK” w menu, Zasoby → Kartoteki). */
export function CepikVerificationPage() {
  return <CepikVerificationView />;
}

export function CepikVerificationView({ modeSwitch }: { modeSwitch?: ReactNode }) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canRun = can('drivers.cepik.run');
  const [params, setParams] = useSearchParams();
  const focusAlert = params.get('alert');
  const [sub, setSub] = useState<SubView>(focusAlert ? 'alerts' : 'drivers');
  useEffect(() => { if (focusAlert) setSub('alerts'); }, [focusAlert]);

  const ovQ = useQuery({
    queryKey: ['cepik', 'overview'],
    queryFn: () => api.get<CepikOverview>('/cepik-verification/overview'),
    refetchInterval: (q) => ((q.state.data as CepikOverview | undefined)?.activeRun ? 3000 : 30_000),
  });
  const ov = ovQ.data;
  const running = ov?.activeRun ?? null;

  const listQ = useQuery({
    queryKey: ['cepik', 'drivers'],
    queryFn: () => api.get<CepikDriverRow[]>('/cepik-verification/drivers'),
    refetchInterval: running ? 10_000 : false,
  });
  const alertCountsQ = useQuery({
    queryKey: ['cepik', 'alert-counts'],
    queryFn: () => api.get<Record<string, number>>('/cepik-verification/alerts/counts'),
    refetchInterval: 60_000,
  });
  const openAlerts = Object.values(alertCountsQ.data ?? {}).reduce((a, b) => a + b, 0);
  const openCritical = alertCountsQ.data?.CRITICAL ?? 0;

  // Koniec przebiegu → świeża lista i alerty.
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (running) setWasRunning(true);
    else if (wasRunning) {
      setWasRunning(false);
      qc.invalidateQueries({ queryKey: ['cepik'] });
    }
  }, [running, wasRunning, qc]);

  const rows = listQ.data ?? [];
  const active = useMemo(() => rows.filter((r) => r.isActive), [rows]);

  // B-704: od kliknięcia do odpowiedzi serwera przyciski są zablokowane — drugie kliknięcie nie wyśle drugiego przebiegu.
  const startingRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const startRun = async (body: Record<string, unknown>, what: string) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      const r = await api.post<CepikRun>('/cepik-verification/runs', body);
      toast.success(`Uruchomiono sprawdzanie: ${what} (${r.total})`);
      await qc.invalidateQueries({ queryKey: ['cepik', 'overview'] });
    } catch (e) {
      toast.error(errMsg(e, 'Nie udało się uruchomić sprawdzania'));
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };
  const cancelRun = async () => {
    if (!running) return;
    try {
      await api.post(`/cepik-verification/runs/${running.id}/cancel`);
      toast.success('Przebieg zostanie przerwany po bieżącym kierowcy');
      qc.invalidateQueries({ queryKey: ['cepik', 'overview'] });
    } catch (e) { toast.error(errMsg(e, 'Nie udało się przerwać')); }
  };

  const [bucketFilter, setBucketFilter] = useState<StateFilter>('issues');
  const [typeFilter, setTypeFilter] = useState<DriverTypeFilter>('');
  // B-422: „Wymaga reakcji” — wspólny filtr kafla w hero i szybkiego filtra listy.
  const [needsAction, setNeedsAction] = useState(false);
  const needsCount = active.filter((r) => r.openAlerts).length;
  const counts = ov?.counts;

  return (
    <div className="flex flex-col gap-4">
      <ViewHero icon={ShieldCheck} color="#0d9488" title="Weryfikacja CEPiK"
        subtitle="Stan uprawnień kierowców w Centralnej Ewidencji Pojazdów i Kierowców — niezależnie od danych z GAiT"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {modeSwitch}
            {canRun && (
              <button type="button" disabled={starting || !!running || !ov?.configured}
                onClick={() => { if (confirm(`Sprawdzić teraz w CEPiK wszystkich aktywnych kierowców (${ov?.total ?? 0})?`)) void startRun({ scope: 'ALL_ACTIVE' }, 'wszyscy aktywni'); }}
                className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-teal-700 shadow hover:bg-white/90 disabled:opacity-50">
                <RefreshCw className="h-4 w-4" /> Sprawdź wszystkich aktywnych
              </button>
            )}
          </div>
        }>
        {counts && (
          <>
            <div className="flex flex-wrap gap-2">
              <HeroKpi icon={BellRing} label="Wymaga reakcji" value={needsCount} color={BUCKET.lost.hex}
                alert={needsCount > 0}
                active={needsAction}
                onClick={() => { setSub('drivers'); if (!needsAction) setBucketFilter(''); setNeedsAction((v) => !v); }}
                title="Kierowcy z niepotwierdzonym alertem — pokaż tylko ich" />
              {BUCKET_ORDER.map((b) => {
                const m = BUCKET[b];
                return (
                  <HeroKpi key={b} icon={m.icon} label={m.label} value={counts[b] ?? 0} color={m.hex}
                    alert={b === 'lost' && (counts.lost ?? 0) > 0}
                    active={bucketFilter === b}
                    onClick={() => { setSub('drivers'); setBucketFilter((f) => (f === b ? '' : b)); }}
                    title={b === 'expiring' ? `Najbliższy termin w ciągu ${ov.expiringDays} dni` : m.label} />
                );
              })}
            </div>
            <HeroHealthBar icon={ShieldCheck} title={`Aktywni kierowcy (${ov.total}) wg stanu w CEPiK`}
              segments={BUCKET_ORDER.map((b) => ({
                key: b, label: BUCKET[b].short.toLowerCase(), value: counts[b] ?? 0, color: BUCKET[b].hex,
                active: bucketFilter === b, onClick: () => { setSub('drivers'); setBucketFilter((f) => (f === b ? '' : b)); },
              }))} />
          </>
        )}
      </ViewHero>

      {ov && !ov.configured && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Oficjalne API CEPiK nie jest skonfigurowane na tym serwerze — sprawdzanie masowe i harmonogram nie działają.
          Pojedyncze sprawdzenie w karcie kierowcy korzysta wtedy ze strony gov.pl.
        </div>
      )}

      <RunStrip ov={ov} canRun={canRun} onCancel={cancelRun} />

      <div className="flex flex-wrap items-center gap-2">
        <Segmented<SubView> value={sub} onChange={(v) => { setSub(v); if (v !== 'alerts' && focusAlert) { params.delete('alert'); setParams(params, { replace: true }); } }}
          options={[
            { value: 'drivers', label: `Kierowcy (${active.length})` },
            { value: 'alerts', label: openAlerts ? `Alerty (${openAlerts})` : 'Alerty' },
            { value: 'expiry', label: 'Wygasania — 90 dni' },
          ]} />
        {openCritical > 0 && sub !== 'alerts' && (
          <button type="button" onClick={() => setSub('alerts')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-2.5 py-1 text-xs font-bold text-white shadow-sm hover:bg-red-700">
            <Bell className="h-3.5 w-3.5" /> {openCritical} niepotwierdzon{openCritical === 1 ? 'y alert' : 'e alerty'} krytyczn{openCritical === 1 ? 'y' : 'e'}
          </button>
        )}
        <span className="ml-auto" />
        <ReportButton type={typeFilter} />
        {can('drivers.cepik.settings') && (
          <Link to="/admin/system?sekcja=cepik"
            className="inline-flex items-center gap-1.5 rounded-xl border bg-muted/40 px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
            <Settings2 className="h-4 w-4" /> Harmonogram i przebiegi
          </Link>
        )}
      </div>

      {sub === 'drivers' && (
        <DriversList rows={rows} loading={listQ.isLoading} bucketFilter={bucketFilter} setBucketFilter={setBucketFilter}
          canRun={canRun} busy={starting || !!running || !ov?.configured} onRun={startRun}
          type={typeFilter} setType={setTypeFilter}
          needsAction={needsAction} setNeedsAction={setNeedsAction}
          onOpenAlert={(id) => { params.set('alert', id); setParams(params); }} />
      )}
      {sub === 'alerts' && <AlertsList focusId={focusAlert} />}
      {sub === 'expiry' && <ExpiryCalendar rows={active} />}
    </div>
  );
}

// ─── Pasek bieżącego / ostatniego przebiegu ─────────────────────────────────

function RunStrip({ ov, canRun, onCancel }: { ov?: CepikOverview; canRun: boolean; onCancel: () => void }) {
  if (!ov) return null;
  const r = ov.activeRun;
  if (r) {
    const pct = r.total ? Math.round((r.cursor / r.total) * 100) : 0;
    return (
      <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4 dark:border-sky-500/30 dark:bg-sky-500/10">
        <div className="flex flex-wrap items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
          <span className="text-sm font-semibold">Trwa sprawdzanie w CEPiK — {r.scopeLabel}</span>
          <span className="text-xs text-muted-foreground">{TRIGGER_LABEL[r.trigger]}{r.startedByName ? ` · ${r.startedByName}` : ''} · start {fmtDateTime(r.startedAt ?? r.createdAt)}</span>
          {canRun && (
            <button type="button" onClick={onCancel} disabled={r.cancelRequested}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-50">
              <StopCircle className="h-3.5 w-3.5" /> {r.cancelRequested ? 'Przerywanie…' : 'Przerwij'}
            </button>
          )}
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-sky-100 dark:bg-sky-500/20">
          <div className="h-full rounded-full bg-sky-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span><b className="tabular-nums text-foreground">{r.cursor}</b> z {r.total} ({pct}%)</span>
          <RunCounts r={r} />
        </div>
      </div>
    );
  }
  const l = ov.lastRun;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-xl border bg-card px-4 py-2.5 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5"><History className="h-3.5 w-3.5" />
        Ostatni przebieg: {l ? <><b className="text-foreground">{fmtDateTime(l.finishedAt ?? l.createdAt)}</b> · {l.scopeLabel} · <span className={cn('rounded px-1.5 py-0.5 font-bold', RUN_STATUS[l.status].cls)}>{RUN_STATUS[l.status].label}</span></> : 'jeszcze nie było'}
      </span>
      <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />
        Następny z harmonogramu: {ov.schedule.enabled && ov.schedule.next ? <b className="text-foreground">{fmtDateTime(ov.schedule.next)}</b> : <b className="text-foreground">harmonogram wyłączony</b>}
      </span>
      {l && <RunCounts r={l} />}
    </div>
  );
}

export function RunCounts({ r, compact }: { r: Pick<CepikRun, 'valid' | 'lost' | 'warn' | 'notFound' | 'noBlankiet' | 'errors' | 'changed'>; compact?: boolean }) {
  // `compact` — tylko niezerowe (i zawsze „ważne”), do wąskiej kolumny tabeli przebiegów.
  const item = (label: string, n: number, cls?: string) => (compact && n === 0 && label !== 'ważne' ? null : (
    <span className="whitespace-nowrap">{label} <b className={cn('tabular-nums', n > 0 && cls ? cls : 'text-foreground')}>{n}</b></span>
  ));
  return (
    <span className="inline-flex flex-wrap gap-x-3">
      {item('ważne', r.valid)}
      {item('brak ważnych', r.lost, 'text-red-600')}
      {item('do wyjaśnienia', r.warn, 'text-orange-600')}
      {item('nie znaleziono', r.notFound)}
      {item('brak druku', r.noBlankiet)}
      {item('błędy', r.errors, 'text-rose-600')}
      {item('zmiany', r.changed, 'text-sky-600')}
    </span>
  );
}

// ─── Raport PDF ─────────────────────────────────────────────────────────────

const TYPE_REPORT_LABEL: Record<'DRIVER' | 'MOTORMAN', string> = { DRIVER: 'kierowcy', MOTORMAN: 'motorniczowie' };

function ReportButton({ type }: { type: DriverTypeFilter }) {
  const months = useMemo(() => {
    const out: string[] = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
      out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, []);
  const [month, setMonth] = useState(months[0]);
  const [busy, setBusy] = useState(false);
  const label = (m: string) => new Date(`${m}-15`).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  const go = async () => {
    setBusy(true);
    // B-705: raport w kontekście filtra kierowcy / motorniczowie (bez filtra — wszyscy).
    const suffix = type ? `-${TYPE_REPORT_LABEL[type]}` : '';
    try { await downloadFromApi(`/cepik-verification/report.pdf?month=${month}${type ? `&type=${type}` : ''}`, `raport-kontroli-uprawnien-${month}${suffix}.pdf`); }
    catch (e) { toast.error(errMsg(e, 'Nie udało się przygotować raportu')); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1 rounded-xl border bg-muted/30 p-1" title={`Raport kontroli uprawnień (PDF) — ${type ? `tylko ${TYPE_REPORT_LABEL[type]}, wg filtra listy` : 'wszyscy'}; dowód sprawdzania dla kontroli`}>
      <Lista value={month} onChange={(e) => setMonth(e.target.value)} className="h-7 rounded-lg bg-transparent px-1.5 text-xs font-semibold focus:outline-none">
        {months.map((m) => <option key={m} value={m}>{label(m)}</option>)}
      </Lista>
      <button type="button" onClick={go} disabled={busy}
        className="inline-flex h-7 items-center gap-1.5 rounded-lg bg-foreground px-2.5 text-xs font-semibold text-background disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Raport PDF{type ? ` — ${TYPE_REPORT_LABEL[type]}` : ''}
      </button>
    </div>
  );
}

// ─── Lista kierowców ────────────────────────────────────────────────────────

function DriversList({ rows, loading, bucketFilter, setBucketFilter, canRun, busy, onRun, onOpenAlert, needsAction, setNeedsAction, type, setType }: {
  rows: CepikDriverRow[];
  type: DriverTypeFilter;
  setType: Dispatch<SetStateAction<DriverTypeFilter>>;
  onOpenAlert: (alertId: string) => void;
  needsAction: boolean;
  setNeedsAction: (v: boolean | ((prev: boolean) => boolean)) => void;
  loading: boolean;
  bucketFilter: StateFilter;
  setBucketFilter: (b: StateFilter) => void;
  canRun: boolean;
  busy: boolean;
  onRun: (body: Record<string, unknown>, what: string) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [depot, setDepot] = useState('');
  const [stale, setStale] = useState('');
  const [duty, setDuty] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<CepikDriverRow | null>(null);

  const depots = useMemo(() => [...new Set(rows.map((r) => r.depotCode).filter(Boolean) as string[])].sort(), [rows]);
  // B-497: liczniki do rozwijanego filtra „Stan w CEPiK” (zgodne z przełącznikiem „Pokaż nieaktywnych”).
  const stateCounts = useMemo(() => {
    const c: Partial<Record<Bucket, number>> = {};
    for (const r of rows) if (showInactive || r.isActive) c[r.bucket] = (c[r.bucket] ?? 0) + 1;
    return c;
  }, [rows, showInactive]);
  const issuesCount = Object.entries(stateCounts).reduce((n, [b, k]) => (b === 'valid' ? n : n + (k ?? 0)), 0);
  // Wszystkie filtry poza „Wymaga reakcji” — z tego liczy się chip i lista.
  const base = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = stale ? Date.now() - Number(stale) * 86_400_000 : null;
    const order = new Map(BUCKET_ORDER.map((b, i) => [b, i]));
    return rows
      .filter((r) => showInactive || r.isActive)
      .filter((r) => !bucketFilter || (bucketFilter === 'issues' ? r.bucket !== 'valid' : r.bucket === bucketFilter))
      .filter((r) => !depot || r.depotCode === depot)
      .filter((r) => !type || (type === 'MOTORMAN' ? r.driverType === 'MOTORMAN' : r.driverType !== 'MOTORMAN')) // jak grupy raportu
      .filter((r) => !duty || r.onDutyToday || r.onDutyTomorrow)
      .filter((r) => !cutoff || !r.cepikCheckedAt || new Date(r.cepikCheckedAt).getTime() < cutoff)
      .filter((r) => !q || `${r.lastName} ${r.firstName} ${r.serviceNumber ?? ''}`.toLowerCase().includes(q))
      // Kolejność bez zmian względem sprzed B-422 (decyzja właściciela) — „wymaga reakcji” tylko oznacza, nie przestawia.
      .sort((a, b) => (order.get(a.bucket)! - order.get(b.bucket)!) || a.lastName.localeCompare(b.lastName, 'pl'));
  }, [rows, showInactive, bucketFilter, depot, type, duty, stale, search]);
  const filtered = useMemo(() => (needsAction ? base.filter((r) => !!r.openAlerts) : base), [base, needsAction]);
  const needsCount = base.filter((r) => !!r.openAlerts).length;

  const [limit, setLimit] = useState(150);
  useEffect(() => setLimit(150), [filtered.length]);
  const visible = filtered.slice(0, limit);

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someSelected = filtered.some((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => {
    const next = new Set(s);
    if (allSelected) filtered.forEach((r) => next.delete(r.id)); else filtered.forEach((r) => next.add(r.id));
    return next;
  });
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const filters: ActiveFilter[] = ([
    search && { key: 'q', label: `Szukaj: „${search}”`, tone: 'slate', onClear: () => setSearch('') },
    bucketFilter && (bucketFilter === 'issues'
      ? { key: 'b', label: 'Nieprawidłowości (bez „Ważne”)', tone: 'amber', onClear: () => setBucketFilter('') }
      : { key: 'b', label: BUCKET[bucketFilter].label, tone: BUCKET[bucketFilter].tone, onClear: () => setBucketFilter('') }),
    depot && { key: 'd', label: `Zajezdnia ${depotLabel(depot)}`, tone: 'indigo', onClear: () => setDepot('') },
    type && { key: 't', label: type === 'MOTORMAN' ? 'Motorniczy' : 'Kierowcy', tone: type === 'MOTORMAN' ? 'purple' : 'sky', onClear: () => setType('') },
    stale && { key: 's', label: STALE_OPTS.find((o) => o.value === stale)!.label, tone: 'amber', onClear: () => setStale('') },
    duty && { key: 'duty', label: 'Na służbie dziś/jutro', tone: 'teal', onClear: () => setDuty(false) },
    needsAction && { key: 'na', label: 'Wymaga reakcji', tone: 'red', onClear: () => setNeedsAction(false) },
  ] as (ActiveFilter | '' | false)[]).filter((f): f is ActiveFilter => !!f);

  // Zaznaczenie liczy się tylko wśród widocznych (przefiltrowanych) — ukryte filtrem nie trafiają do „Sprawdź zaznaczonych”.
  const selIds = filtered.filter((r) => selected.has(r.id)).map((r) => r.id);
  const hiddenSelected = selected.size - selIds.length;
  return (
    <>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          <SearchBox value={search} onChange={setSearch} placeholder="Szukaj po nazwisku, numerze służbowym…" />
          {depots.length > 0 && (
            <FilterSelect icon={MapPin} tone="indigo" value={depot} onChange={setDepot} title="Zajezdnia">
              <option value="">Wszystkie zajezdnie</option>
              {depots.map((d) => <option key={d} value={d}>Zaj. {depotLabel(d)}</option>)}
            </FilterSelect>
          )}
          <FilterSelect icon={ShieldCheck} tone="teal" value={bucketFilter} onChange={(v) => setBucketFilter(v as StateFilter)} title="Stan w CEPiK">
            <option value="issues">Nieprawidłowości — wszystko poza „Ważne” ({issuesCount})</option>
            <option value="">Wszystkie stany</option>
            {BUCKET_ORDER.map((b) => <option key={b} value={b}>{BUCKET[b].label} ({stateCounts[b] ?? 0})</option>)}
          </FilterSelect>
          <FilterSelect icon={Clock} tone="amber" value={stale} onChange={setStale} title="Data ostatniego sprawdzenia">
            {STALE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </FilterSelect>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
          <QuickChip tone="red" icon={BellRing} label="Wymaga reakcji" active={needsAction} onClick={() => setNeedsAction((v) => !v)} count={needsCount} />
          <QuickChip tone="sky" icon={Bus} label="Kierowcy" active={type === 'DRIVER'} onClick={() => setType((t) => (t === 'DRIVER' ? '' : 'DRIVER'))} />
          <QuickChip tone="purple" icon={TramFront} label="Motorniczy" active={type === 'MOTORMAN'} onClick={() => setType((t) => (t === 'MOTORMAN' ? '' : 'MOTORMAN'))} />
          <QuickChip tone="teal" icon={CalendarClock} label="Na służbie dziś/jutro" active={duty} onClick={() => setDuty((d) => !d)}
            count={rows.filter((r) => r.onDutyToday || r.onDutyTomorrow).length} />
          <QuickChip tone="slate" label="Pokaż nieaktywnych" active={showInactive} onClick={() => setShowInactive((v) => !v)} />
        </div>
        <ActiveFilterBar filters={filters} shown={filtered.length} total={rows.filter((r) => showInactive || r.isActive).length}
          onClearAll={() => { setSearch(''); setBucketFilter(''); setDepot(''); setType(''); setStale(''); setDuty(false); setNeedsAction(false); }} />
        {canRun && (
          <div className="flex flex-wrap items-center gap-2 border-t bg-muted/20 px-3 py-2">
            <span className="text-xs text-muted-foreground">Zaznaczono <b className="tabular-nums text-foreground">{selIds.length}</b>{hiddenSelected > 0 && ` (+${hiddenSelected} ukrytych filtrem)`}</span>
            <button type="button" disabled={busy || !selIds.length}
              onClick={() => onRun({ driverIds: selIds, scopeLabel: `zaznaczeni (${selIds.length})` }, 'zaznaczeni').then(() => setSelected(new Set()))}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700 disabled:opacity-40">
              <PlayCircle className="h-3.5 w-3.5" /> Sprawdź zaznaczonych
            </button>
            <button type="button" disabled={busy || !filtered.length}
              onClick={() => {
                const ids = filtered.map((r) => r.id);
                const what = filters.length ? `widoczni wg filtrów: ${filters.map((f) => f.label).join(', ')}` : 'wszyscy widoczni';
                if (confirm(`Sprawdzić w CEPiK ${ids.length} widocznych kierowców?`)) void onRun({ driverIds: ids, scopeLabel: `${what} (${ids.length})` }, 'widoczni');
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted disabled:opacity-40">
              <RefreshCw className="h-3.5 w-3.5" /> Sprawdź widocznych ({filtered.length})
            </button>
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-xs font-semibold text-muted-foreground hover:text-foreground">Wyczyść zaznaczenie</button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Ładowanie…</div>
      ) : !filtered.length ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-muted-foreground">
          <Users className="h-8 w-8 opacity-30" />
          <p className="text-sm font-semibold">Brak kierowców dla tych filtrów</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  {canRun && (
                    <th className="w-10 px-3 py-2.5">
                      <button type="button" onClick={toggleAll} title={allSelected ? 'Odznacz widocznych' : 'Zaznacz widocznych'} className="text-muted-foreground hover:text-foreground">
                        {allSelected ? <CheckSquare className="h-4 w-4" /> : someSelected ? <MinusSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                    </th>
                  )}
                  <th className="px-3 py-2.5">Kierowca</th>
                  <th className="px-3 py-2.5">Zajezdnia</th>
                  <th className="px-3 py-2.5">Stan w CEPiK</th>
                  <th className="px-3 py-2.5">Najbliższy termin</th>
                  <th className="px-3 py-2.5">Sprawdzono</th>
                  <th className="w-24 px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => <DriverRow key={r.id} r={r} canRun={canRun} checked={selected.has(r.id)} onToggle={() => toggle(r.id)} onOpen={() => setDetail(r)} onOpenAlert={onOpenAlert} />)}
              </tbody>
            </table>
          </div>
          {filtered.length > limit && (
            <button type="button" onClick={() => setLimit((l) => l + 300)}
              className="w-full border-t py-2.5 text-sm font-semibold text-muted-foreground hover:bg-muted/40 hover:text-foreground">
              Pokaż kolejnych ({filtered.length - limit} ukrytych)
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {detail && <DriverDrawer key={detail.id} row={rows.find((x) => x.id === detail.id) ?? detail} onClose={() => setDetail(null)} />}
      </AnimatePresence>
    </>
  );
}

const ALERT_KIND_LABEL: Record<string, string> = {
  LOSS: 'brak ważnych uprawnień', CATEGORY_LOST: 'brak kategorii', REPLACED: 'wymiana dokumentu', OTHER: 'nietypowy stan dokumentu',
  NOT_FOUND: 'dokument zniknął z CEPiK', RESTORED: 'przywrócenie uprawnień', EXPIRING: 'wygasanie', MISMATCH: 'rozbieżność GAiT ↔ CEPiK',
};

function DriverRow({ r, canRun, checked, onToggle, onOpen, onOpenAlert }: {
  r: CepikDriverRow; canRun: boolean; checked: boolean; onToggle: () => void; onOpen: () => void; onOpenAlert: (id: string) => void;
}) {
  const oa = r.openAlerts;
  const ne = r.cepikNearestExpiry?.slice(0, 10) ?? null;
  const days = ne ? daysLeft(ne) : null;
  const today = todayPl();
  const mismatch = r.externalSource && r.cepikStatus && LOSS.has(r.cepikStatus) && r.licenseExpiry && r.licenseExpiry.slice(0, 10) >= today;
  const lost = r.bucket === 'lost';
  const why = lost ? lossSummary(r, today) : null;
  // Utrata ma być widoczna z daleka: czerwony pasek z lewej (inset — nie przesuwa kolumn),
  // mocniejsze tło, ikona przy nazwisku i zdanie „dlaczego” pod stanem.
  // B-422: niepotwierdzony alert bez utraty (np. wygasanie, rozbieżność) — bursztynowy pasek i lekkie tło.
  const stripe = lost ? 'shadow-[inset_4px_0_0_0_rgb(220_38_38)]' : oa ? (oa.critical ? 'shadow-[inset_4px_0_0_0_rgb(220_38_38)]' : 'shadow-[inset_4px_0_0_0_rgb(217_119_6)]') : '';
  return (
    <tr className={cn('border-b last:border-0 transition-colors',
      lost ? 'bg-red-100/70 hover:bg-red-100 dark:bg-red-500/[0.14] dark:hover:bg-red-500/20'
        : oa ? 'bg-amber-50 hover:bg-amber-100/70 dark:bg-amber-500/[0.08] dark:hover:bg-amber-500/[0.14]' : 'hover:bg-muted/30',
      !r.isActive && 'opacity-60')}>
      {canRun && (
        <td className={cn('px-3 py-2.5', stripe)}>
          <button type="button" onClick={onToggle} aria-pressed={checked} className="text-muted-foreground hover:text-foreground">
            {checked ? <CheckSquare className="h-4 w-4 text-teal-600" /> : <Square className="h-4 w-4" />}
          </button>
        </td>
      )}
      <td className={cn('px-3 py-2.5', !canRun && stripe)}>
        <button type="button" onClick={onOpen} className="text-left">
          <span className={cn('flex items-center gap-1.5 font-semibold hover:underline', lost && 'text-red-700 dark:text-red-300')}>
            {lost && <ShieldX className="h-4 w-4 shrink-0" aria-label={BUCKET.lost.label} />}
            {r.lastName} {r.firstName}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            {r.serviceNumber && <span>nr {r.serviceNumber}</span>}
            {r.driverType === 'MOTORMAN' ? <span className="inline-flex items-center gap-0.5"><TramFront className="h-3 w-3" />motorniczy</span> : r.driverType === 'DRIVER' ? <span className="inline-flex items-center gap-0.5"><Bus className="h-3 w-3" />kierowca</span> : null}
            {(r.onDutyToday || r.onDutyTomorrow) && (
              <span className="rounded bg-teal-100 px-1 font-semibold text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">służba {r.onDutyToday ? 'dziś' : 'jutro'}</span>
            )}
            {!r.isActive && <span>nieaktywny</span>}
          </span>
        </button>
        {oa && (
          <button type="button" onClick={() => onOpenAlert(oa.firstId)}
            title={`Niepotwierdzone alerty: ${oa.kinds.map((k) => ALERT_KIND_LABEL[k] ?? k).join(', ')} — kliknij, żeby obsłużyć`}
            className={cn('mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold',
              oa.critical ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-amber-500 text-white hover:bg-amber-600')}>
            <BellRing className="h-3 w-3" /> Wymaga reakcji{oa.count > 1 ? ` · ${oa.count}` : ''}
          </button>
        )}
      </td>
      <td className="px-3 py-2.5">{r.depotCode ? <DepotChip code={r.depotCode} /> : <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2.5">
        <div className="flex flex-col items-start gap-0.5">
          <BucketPill bucket={r.bucket} />
          {why ? (
            <span className="max-w-[20rem] text-[12px] font-semibold leading-snug text-red-700 dark:text-red-300">{why}</span>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {r.bucket === 'error' ? `Błąd: ${r.cepikLastError ?? ''}` : r.cepikStan ? `Stan: ${r.cepikStan}` : r.bucket === 'noBlankiet' ? 'Uzupełnij numer druku w karcie' : r.bucket === 'invalid' ? 'Popraw numer dokumentu w kartotece' : ''}
            </span>
          )}
          {r.cepikProblem && (
            <span className={cn('text-[11px]', r.cepikProblem.everFound ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}
              title={`Złych sprawdzeń z rzędu: ${r.cepikProblem.count}`}>
              {problemText(r.cepikProblem)}
            </span>
          )}
          {why && r.cepikStan && <span className="text-[11px] text-muted-foreground">stan dokumentu: {r.cepikStan}</span>}
          {mismatch && <span className="text-[11px] font-semibold text-red-700 dark:text-red-300">W GAiT: ważne do {fmtDate(r.licenseExpiry!.slice(0, 10))}</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 tabular-nums">
        {ne ? (
          <span className={cn(days! < 0 ? 'text-red-600' : days! <= 30 ? 'text-amber-600 font-semibold' : '')}>
            {fmtDate(ne)} <span className="text-[11px] text-muted-foreground">({relDays(days!)})</span>
          </span>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2.5 text-[12px] text-muted-foreground">{fmtDateTime(r.cepikCheckedAt)}</td>
      <td className="px-3 py-2.5 text-right">
        <button type="button" onClick={onOpen} className="rounded-lg px-2 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-500/10">Szczegóły</button>
      </td>
    </tr>
  );
}

// ─── Szuflada kierowcy ──────────────────────────────────────────────────────

function DriverDrawer({ row: r, onClose }: { row: CepikDriverRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const histQ = useQuery({
    queryKey: ['cepik', 'history', r.id],
    queryFn: () => api.get<CepikCheck[]>(`/cepik-verification/drivers/${r.id}/checks`),
  });
  const [busy, setBusy] = useState(false);
  const [blankiet, setBlankiet] = useState('');

  const checkNow = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ outcome: string; status: string | null; changed: boolean; error: string | null }>(`/cepik-verification/drivers/${r.id}/check`);
      if (res.outcome === 'ERROR') toast.error(`CEPiK: ${res.error ?? 'błąd połączenia'}`);
      else toast.success(`Sprawdzono: ${STATUS_LABEL[res.status ?? ''] ?? res.status}${res.changed ? ' — wynik się zmienił' : ''}`);
      qc.invalidateQueries({ queryKey: ['cepik'] });
      qc.invalidateQueries({ queryKey: ['drivers'] });
    } catch (e) { toast.error(errMsg(e, 'Nie udało się sprawdzić')); }
    finally { setBusy(false); }
  };
  const saveBlankiet = async () => {
    setBusy(true);
    try {
      await api.patch(`/cepik-verification/drivers/${r.id}/blankiet`, { blankietNo: blankiet });
      toast.success('Numer druku zapisany — sprawdzam w CEPiK');
      setBusy(false);
      await checkNow();
    } catch (e) { toast.error(errMsg(e, 'Nie udało się zapisać numeru')); setBusy(false); }
  };

  const cats = r.cepikCategories ?? [];
  const today = todayPl();
  const workCats = new Set(workCategories(cats, r.driverType).map((c) => c.kategoria));
  const gaitCats = new Set(r.licenseCategories.map((c) => c.toUpperCase()));
  // Motorniczy nie mają kategorii PJ w GAiT — tam jest „Pozwolenie” (kat. U w CEPiK).
  // Pokazujemy uprawnienia z kartoteki inne niż prawo jazdy (to już jest w kategoriach).
  const gaitPermits = (r.gaitPermits ?? []).filter((p) => !/^prawo jazdy/i.test(p.name));
  const cepikCats = new Set(cats.filter((c) => !c.dataWaznosci || c.dataWaznosci >= today).map((c) => c.kategoria.toUpperCase()));
  const onlyGait = [...gaitCats].filter((c) => !cepikCats.has(c));

  return (
    <div className="fixed inset-0 z-[3500] flex justify-end bg-black/40 backdrop-blur-[1px]" onClick={onClose}>
      <motion.aside initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ duration: 0.18 }}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l bg-background shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b bg-background/95 px-5 py-4 backdrop-blur">
          <div className="min-w-0 flex-1">
            <p className="text-lg font-bold leading-tight">{r.firstName} {r.lastName}</p>
            <p className="text-xs text-muted-foreground">
              {[r.serviceNumber && `nr ${r.serviceNumber}`, r.depotCode && `zaj. ${depotLabel(r.depotCode)}`, r.driverType === 'MOTORMAN' ? 'motorniczy' : r.driverType === 'DRIVER' ? 'kierowca' : null, r.externalSource ? 'kartoteka z GAiT' : 'kartoteka ręczna'].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-muted" aria-label="Zamknij"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 p-5">
          <div className={cn('rounded-xl border p-4', r.bucket === 'lost' ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10' : 'bg-card')}>
            <div className="flex flex-wrap items-center gap-2">
              <BucketPill bucket={r.bucket} />
              {r.cepikStatus && <span className="text-sm font-semibold">{STATUS_LABEL[r.cepikStatus] ?? r.cepikStatus}</span>}
              {r.cepikStan && <span className="text-xs text-muted-foreground">stan dokumentu: {r.cepikStan}</span>}
            </div>
            {lossSummary(r, today) && <p className="mt-2 text-sm font-semibold text-red-700 dark:text-red-300">{lossSummary(r, today)}</p>}
            {r.cepikStatus && !LOSS.has(r.cepikStatus) && realReasons(r.cepikReasons).length > 0 && (
              <p className="mt-2 text-sm">Powód zmiany stanu: {realReasons(r.cepikReasons).join('; ')}</p>
            )}
            {r.cepikLastError && <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">Ostatnia próba {fmtDateTime(r.cepikAttemptAt)} nieudana: {r.cepikLastError}</p>}
            <p className="mt-2 text-xs text-muted-foreground">
              Ostatnia odpowiedź CEPiK: {fmtDateTime(r.cepikCheckedAt)}{r.cepikChangedAt ? ` · ostatnia zmiana wyniku: ${fmtDateTime(r.cepikChangedAt)}` : ''}
              {r.docKind && ` · sprawdzany dokument: ${r.docKind === 'PT' ? 'pozwolenie tramwajowe' : 'prawo jazdy'}`}
            </p>
            {can('drivers.cepik.run') && r.hasDocument && (
              <button type="button" onClick={checkNow} disabled={busy}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sprawdź teraz
              </button>
            )}
          </div>

          {!r.hasDocument && (
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm dark:border-violet-500/30 dark:bg-violet-500/10">
              <p className="font-semibold">Brak numeru druku prawa jazdy</p>
              <p className="mt-1 text-xs text-muted-foreground">CEPiK szuka po numerze druku (blankietu), nie po numerze prawa jazdy. Numer jest na odwrocie dokumentu (np. AA1234567).</p>
              {can('drivers.manage') && can('drivers.cepik.run') ? (
                <div className="mt-3 flex gap-2">
                  <input value={blankiet} onChange={(e) => setBlankiet(e.target.value.toUpperCase())} placeholder="AA1234567"
                    className="w-40 rounded-lg border bg-background px-3 py-1.5 font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  <button type="button" onClick={saveBlankiet} disabled={busy || blankiet.trim().length < 5}
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50">Zapisz i sprawdź</button>
                </div>
              ) : <p className="mt-2 text-xs">Uzupełnij numer w karcie kierowcy.</p>}
            </div>
          )}

          {r.bucket === 'invalid' && (
            <div className="flex gap-2 rounded-xl border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 dark:border-purple-500/30 dark:bg-purple-500/10 dark:text-purple-200">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                CEPiK odrzucił samo zapytanie jako niepoprawne — <b>nikogo nie sprawdził</b>, więc to nie znaczy, że kierowcy nie ma w rejestrze.
                Najczęściej numer {r.driverType === 'MOTORMAN' ? 'pozwolenia tramwajowego' : 'prawa jazdy'} jest zapisany w formacie, którego CEPiK nie przyjmuje
                (inna liczba liter w serii, znaki spoza liter i cyfr). Spacje, myślniki i kropki system usuwa sam. Porównaj numer z dokumentem
                {r.externalSource ? ' i popraw go w GAiT — numer pochodzi stamtąd i synchronizacja nadpisze ręczną zmianę' : ' i popraw go w karcie kierowcy'}.
              </span>
            </div>
          )}

          {r.bucket === 'notFound' && (
            <div className="flex gap-2 rounded-xl border bg-muted/30 p-3 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {r.cepikProblem && (r.cepikProblem.everFound
                  ? <><b>Był znajdowany do {fmtDate(r.cepikProblem.lastFoundAt)}, od {fmtDate(r.cepikProblem.since)} nie</b> (złych sprawdzeń: {r.cepikProblem.count}). To typowy objaw wymiany dokumentu (nowy numer druku) albo zmiany nazwiska — jeśli GAiT ma już nowy numer, kliknij „Sprawdź teraz”. </>
                  : <><b>Ani razu nie znaleziono, od {fmtDate(r.cepikProblem.since)}</b> (złych sprawdzeń: {r.cepikProblem.count}). Najczęściej literówka w numerze albo dane niezgodne z dokumentem. </>)}
                CEPiK nie znalazł dokumentu. Najczęstsze przyczyny: wymiana prawa jazdy (nowy numer druku), literówka w numerze, inne nazwisko w dokumencie niż w kartotece (np. po ślubie). W zapytaniu wysyłane jest tylko pierwsze imię.</span>
            </div>
          )}

          <section className="rounded-xl border bg-card">
            <h3 className="border-b px-4 py-2.5 text-sm font-semibold">Porównanie: GAiT ↔ CEPiK</h3>
            <div className="grid grid-cols-[8rem_1fr_1fr] gap-x-3 gap-y-2 px-4 py-3 text-sm">
              <span />
              <span className="text-[11px] font-bold uppercase text-muted-foreground">{r.externalSource ? 'GAiT' : 'Kartoteka'}</span>
              <span className="text-[11px] font-bold uppercase text-muted-foreground">CEPiK</span>
              <span className="text-muted-foreground">Kategorie</span>
              <div className="space-y-0.5">
                {r.licenseCategories.length > 0 && <p>{r.licenseCategories.join(', ')}</p>}
                {gaitPermits.map((p, i) => {
                  const expired = !!p.expiresAt && p.expiresAt < today;
                  return (
                    <p key={`${p.name}-${i}`} className="leading-tight">
                      <span className={cn('font-semibold', expired && 'text-red-600 line-through')}>{p.name}</span>
                      <span className={cn('block text-[11px]', expired ? 'font-semibold text-red-600' : 'text-muted-foreground')}>{catValidity(p.expiresAt, expired)}</span>
                    </p>
                  );
                })}
                {!r.licenseCategories.length && !gaitPermits.length && <p>—</p>}
              </div>
              {/* Jedna kategoria w wierszu z datą albo „bezterminowo” — bez tego A/B/B1 bez daty
                  zlewały się z sąsiednią kategorią i wyglądały na niepobrane (zgłoszenie właściciela 29.09). */}
              <ul className="space-y-0.5">{cats.length ? cats.map((c) => {
                const expired = !!c.dataWaznosci && c.dataWaznosci < today;
                // Wygasła kategoria spoza stanowiska (D u motorniczego, kat. spoza D u kierowcy) — widać ją, ale nie blokuje.
                const blocking = expired && workCats.has(c.kategoria);
                return (
                  <li key={c.kategoria} className="flex items-baseline gap-2" title={expired && !blocking ? NOT_WORK_HINT(r.driverType) : undefined}>
                    <span className={cn('w-9 shrink-0 font-semibold', blocking && 'text-red-600 line-through', expired && !blocking && 'text-muted-foreground line-through')}>{c.kategoria}</span>
                    <span className={cn('text-[11px]', blocking ? 'font-semibold text-red-600' : 'text-muted-foreground')}>
                      {catValidity(c.dataWaznosci, expired)}{expired && !blocking && ' · nie blokuje'}
                    </span>
                  </li>
                );
              }) : <li>—</li>}</ul>
              <span className="text-muted-foreground">Ważność</span>
              <span>{r.licenseExpiry ? fmtDate(r.licenseExpiry.slice(0, 10)) : '—'}</span>
              <span>{r.cepikNearestExpiry ? fmtDate(r.cepikNearestExpiry.slice(0, 10)) : '—'}{r.cepikDocExpiry ? <span className="text-[11px] text-muted-foreground"> (dokument do {fmtDate(r.cepikDocExpiry.slice(0, 10))})</span> : null}</span>
            </div>
            {onlyGait.length > 0 && r.cepikStatus === 'VALID' && (
              <p className="border-t px-4 py-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />Kategorie w kartotece, których CEPiK nie potwierdza: {onlyGait.join(', ')}
              </p>
            )}
          </section>

          <section className="rounded-xl border bg-card">
            <h3 className="flex items-center gap-2 border-b px-4 py-2.5 text-sm font-semibold"><History className="h-4 w-4 text-muted-foreground" />Historia sprawdzeń</h3>
            {histQ.isLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : !(histQ.data ?? []).length ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Ten kierowca nie był jeszcze sprawdzany.</p>
            ) : (
              <ol className="relative space-y-3 px-4 py-3">
                {(histQ.data ?? []).map((c) => {
                  const lost = c.status && LOSS.has(c.status);
                  const dot = c.outcome === 'ERROR' ? 'bg-rose-400' : lost ? 'bg-red-500' : c.status === 'VALID' ? 'bg-emerald-500' : 'bg-slate-400';
                  return (
                    <li key={c.id} className="flex gap-3">
                      <span className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', dot, c.changed && 'ring-4 ring-sky-200 dark:ring-sky-500/30')} />
                      <div className="min-w-0 text-sm">
                        <p className="font-semibold">
                          {c.outcome === 'ERROR' ? 'Błąd połączenia' : STATUS_LABEL[c.status ?? ''] ?? c.status}
                          {c.changed && <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">ZMIANA</span>}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {fmtDateTime(c.checkedAt)} · {TRIGGER_LABEL[c.trigger]}{c.userName ? ` · ${c.userName}` : ''}{c.source === 'SCRAPER' ? ' · strona gov.pl' : ''}
                        </p>
                        {(c.stan || c.message) && <p className="text-xs text-muted-foreground">{[c.stan && `stan: ${c.stan}`, c.message].filter(Boolean).join(' · ')}</p>}
                        {!!c.categories?.length && <p className="text-xs text-muted-foreground">kat. {c.categories.map((k) => `${k.kategoria} (${k.dataWaznosci ? `do ${fmtDate(k.dataWaznosci)}` : 'bezterminowo'})`).join(', ')}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>
      </motion.aside>
    </div>
  );
}

// ─── Alerty ─────────────────────────────────────────────────────────────────

const SEVERITY: Record<CepikAlert['severity'], { label: string; stripe: string; badge: string }> = {
  CRITICAL: { label: 'Krytyczny', stripe: 'bg-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
  WARNING:  { label: 'Ostrzeżenie', stripe: 'bg-amber-400', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' },
  INFO:     { label: 'Informacja', stripe: 'bg-sky-400', badge: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
};

function AlertsList({ focusId }: { focusId: string | null }) {
  const [onlyOpen, setOnlyOpen] = useState(true);
  const q = useQuery({
    queryKey: ['cepik', 'alerts', onlyOpen],
    queryFn: () => api.get<CepikAlert[]>(`/cepik-verification/alerts?open=${onlyOpen ? 1 : 0}&take=200`),
    refetchInterval: 60_000,
  });
  const rows = q.data ?? [];
  const qc = useQueryClient();
  const nonCritical = rows.filter((a) => !a.acknowledgedAt && a.severity !== 'CRITICAL').length;
  const ackAll = async () => {
    if (!confirm(`Potwierdzić ${nonCritical} ostrzeżeń i informacji jako przyjęte do wiadomości? Alerty krytyczne zostają.`)) return;
    try {
      const r = await api.post<{ acknowledged: number }>('/cepik-verification/alerts/ack-non-critical');
      toast.success(`Potwierdzono ${r.acknowledged}`);
      qc.invalidateQueries({ queryKey: ['cepik'] });
    } catch (e) { toast.error(errMsg(e, 'Nie udało się potwierdzić')); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented<'open' | 'all'> value={onlyOpen ? 'open' : 'all'} onChange={(v) => setOnlyOpen(v === 'open')}
          options={[{ value: 'open', label: 'Do potwierdzenia' }, { value: 'all', label: 'Wszystkie' }]} />
        {nonCritical > 1 && (
          <button type="button" onClick={ackAll}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted">
            <CheckCircle2 className="h-3.5 w-3.5" /> Potwierdź wszystkie ostrzeżenia ({nonCritical})
          </button>
        )}
      </div>
      {q.isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : !rows.length ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 opacity-30" />
          <p className="text-sm font-semibold">{onlyOpen ? 'Nie ma alertów do potwierdzenia' : 'Brak alertów'}</p>
        </div>
      ) : (
        rows.map((a) => <AlertCard key={a.id} a={a} focused={a.id === focusId} />)
      )}
    </div>
  );
}

export function AlertDetails({ a }: { a: CepikAlert }) {
  const d = a.details ?? {};
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
        {d.driver && <><span className="text-muted-foreground">Kierowca</span><span>{d.driver.name}{d.driver.serviceNumber ? ` · nr ${d.driver.serviceNumber}` : ''}{d.driver.depot ? ` · zaj. ${depotLabel(d.driver.depot)}` : ''}</span></>}
        {(d.status || d.stan) && <><span className="text-muted-foreground">Stan w CEPiK</span><span>
          {d.prevStatus && d.prevStatus !== d.status ? <>{STATUS_LABEL[d.prevStatus] ?? d.prevStatus} → </> : null}
          <b className={cn(d.status && LOSS.has(d.status) && 'text-red-600')}>{d.stan ?? STATUS_LABEL[d.status ?? ''] ?? d.status}</b>
        </span></>}
        {!!d.reasons?.length && <><span className="text-muted-foreground">Powód</span><span>{d.reasons.join('; ')}</span></>}
        {d.why && !(d.reasons ?? []).includes(d.why) && <><span className="text-muted-foreground">Szczegóły</span><span>{d.why}</span></>}
        {!!d.lostCategories?.length && <><span className="text-muted-foreground">Utracone kategorie</span><span className="font-semibold">{d.lostCategories.join(', ')}</span></>}
        {!!d.mismatch?.length && <><span className="text-muted-foreground">Rozbieżność</span><span>{d.mismatch.join('; ')}</span></>}
        {d.gait && <><span className="text-muted-foreground">W GAiT</span><span>kat. {d.gait.categories.join(', ') || '—'}{d.gait.expiry ? `, ważne do ${fmtDate(d.gait.expiry)}` : ''}</span></>}
      </div>
      {!!d.duty?.length && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Zaplanowany na służbę: {d.duty.map((x, i) => (
            <b key={i} className="mr-2">{x.day} {fmtTime(x.start)}{x.line ? `, linia ${x.line}` : ''}{x.brigade ? `, brygada ${x.brigade}` : ''}{x.duty ? ` (${x.duty})` : ''}</b>
          ))}</span>
        </div>
      )}
    </div>
  );
}

function AlertCard({ a, focused }: { a: CepikAlert; focused: boolean }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const s = SEVERITY[a.severity];
  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/cepik-verification/alerts/${a.id}/ack`, { note });
      toast.success('Alert potwierdzony');
      qc.invalidateQueries({ queryKey: ['cepik'] });
    } catch (e) { toast.error(errMsg(e, 'Nie udało się potwierdzić')); }
    finally { setBusy(false); }
  };
  return (
    <div id={`alert-${a.id}`} ref={(el) => { if (el && focused) el.scrollIntoView({ block: 'center' }); }}
      className={cn('flex overflow-hidden rounded-2xl border bg-card', focused && 'ring-2 ring-teal-500', a.acknowledgedAt && 'opacity-75')}>
      <span className={cn('w-1.5 shrink-0', s.stripe)} />
      <div className="min-w-0 flex-1 space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', s.badge)}>{s.label}</span>
          <span className="text-sm font-semibold">{a.title}</span>
          <span className="ml-auto text-[11px] text-muted-foreground">{fmtDateTime(a.createdAt)}</span>
        </div>
        <AlertDetails a={a} />
        <AlertMailHistory a={a} />
        {a.acknowledgedAt ? (
          <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs">
            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />
            Obsłużone przez <b>{a.acknowledgedByName ?? '—'}</b> · {fmtDateTime(a.acknowledgedAt)}{a.note ? ` — „${a.note}”` : ''}
          </p>
        ) : (
          <div className="flex flex-wrap items-start gap-2">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder={a.severity === 'CRITICAL' ? 'Co zrobiono, np. odsunięty od służby, zastępstwo…' : 'Notatka (nieobowiązkowa), np. zaplanowano odnowienie, zaktualizowano blankiet…'}
              className="min-w-[16rem] flex-1 rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <button type="button" onClick={ack} disabled={busy || (a.severity === 'CRITICAL' && note.trim().length < 3)}
              title={a.severity === 'CRITICAL' ? 'Przy braku ważnych uprawnień wpisz, co zrobiono' : 'Notatka nieobowiązkowa'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-bold text-background disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Potwierdź
            </button>
            <AlertMailButton a={a} compact />
          </div>
        )}
        {a.acknowledgedAt && <div className="flex flex-wrap gap-2"><AlertMailButton a={a} compact /></div>}
      </div>
    </div>
  );
}

// ─── Kalendarz wygaśnięć ────────────────────────────────────────────────────

function ExpiryCalendar({ rows }: { rows: CepikDriverRow[] }) {
  const [depot, setDepot] = useState('');
  const depots = useMemo(() => [...new Set(rows.map((r) => r.depotCode).filter(Boolean) as string[])].sort(), [rows]);
  const items = useMemo(() => rows
    .filter((r) => r.cepikNearestExpiry && (r.bucket === 'valid' || r.bucket === 'expiring'))
    .map((r) => ({ r, date: r.cepikNearestExpiry!.slice(0, 10), days: daysLeft(r.cepikNearestExpiry!.slice(0, 10)) }))
    .filter((x) => x.days >= 0 && x.days <= 90 && (!depot || x.r.depotCode === depot))
    .sort((a, b) => a.date.localeCompare(b.date)), [rows, depot]);
  const groups = useMemo(() => {
    const m = new Map<string, typeof items>();
    for (const x of items) m.set(x.date.slice(0, 7), [...(m.get(x.date.slice(0, 7)) ?? []), x]);
    return [...m.entries()];
  }, [items]);
  const expired = rows.filter((r) => r.cepikStatus === 'EXPIRED').length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">Uprawnienia wygasające w ciągu 90 dni wg CEPiK — do zaplanowania odnowień.</p>
        <span className="ml-auto" />
        {depots.length > 0 && (
          <FilterSelect icon={MapPin} tone="indigo" value={depot} onChange={setDepot} title="Zajezdnia">
            <option value="">Wszystkie zajezdnie</option>
            {depots.map((d) => <option key={d} value={d}>Zaj. {depotLabel(d)}</option>)}
          </FilterSelect>
        )}
      </div>
      {expired > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          <ShieldX className="h-4 w-4" /> Już po terminie: <b>{expired}</b> — w widoku „Kierowcy” filtr „Brak ważnych uprawnień”.
        </div>
      )}
      {!groups.length ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-muted-foreground">
          <CalendarRange className="h-8 w-8 opacity-30" />
          <p className="text-sm font-semibold">Nic nie wygasa w najbliższych 90 dniach</p>
        </div>
      ) : groups.map(([month, list]) => (
        <section key={month} className="overflow-hidden rounded-2xl border bg-card">
          <h3 className="flex items-center gap-2 border-b bg-muted/30 px-4 py-2.5 text-sm font-bold capitalize">
            <CalendarRange className="h-4 w-4 text-muted-foreground" />
            {new Date(`${month}-15`).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}
            <span className={cn('rounded-full px-2 text-[11px]', TONE.amber.count)}>{list.length}</span>
          </h3>
          <ul className="divide-y">
            {list.map(({ r, date, days }) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                <span className={cn('w-24 tabular-nums font-semibold', days <= 14 ? 'text-red-600' : days <= 30 ? 'text-amber-600' : '')}>{fmtDate(date)}</span>
                <span className="w-20 text-xs text-muted-foreground">{relDays(days)}</span>
                <span className="min-w-0 flex-1 font-medium">{r.lastName} {r.firstName}{r.serviceNumber ? <span className="text-xs text-muted-foreground"> · nr {r.serviceNumber}</span> : null}</span>
                {r.depotCode && <DepotChip code={r.depotCode} />}
                <span className="text-xs text-muted-foreground">
                  {(r.cepikCategories ?? []).filter((c) => c.dataWaznosci?.slice(0, 10) === date).map((c) => `kat. ${c.kategoria}`).join(', ') || (r.cepikDocExpiry?.slice(0, 10) === date ? 'dokument' : '')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
